/** Townhall API — 주간 타운홀 자료 히스토리. 열람·등록은 전사 직원, 수정·삭제는 등록자 본인. */

export interface TownhallMaterial {
  id: string
  kind: 'file' | 'link'
  name: string
  url: string | null
  ext: string | null
  sizeBytes: number | null
  presenter: string | null
  description: string | null
  /** Keynote 에 붙인 PDF 버전 — Windows 는 내려받기가 자동으로 이 PDF 가 된다. */
  pdf: { name: string; sizeBytes: number | null } | null
  /** Keynote 자동 변환 상태. null = 변환 안 함(빼거나 서버에서 못 함). */
  pdfStatus: 'pending' | 'converting' | 'ready' | 'failed' | null
}

export interface TownhallRow {
  id: string
  /** 진행일 YYYY-MM-DD. */
  date: string
  year: number
  title: string | null
  /** 목차 — 순서대로. */
  agenda: string[]
  /** 발표자 — 자유 입력. */
  presenters: string | null
  /** 등록자 이메일. */
  createdBy: string | null
  /** 내가 등록한 타운홀이면 true — 수정·삭제는 본인만. */
  canEdit: boolean
  materials: TownhallMaterial[]
}

export interface TownhallList {
  /** 서버가 Keynote 를 PDF 로 자동 변환할 수 있는지. */
  pdfConversion: boolean
  maxMaterials: number
  uploadLimitBytes: number
  allowedExtensions: string[]
  townhalls: TownhallRow[]
}

export class TownhallError extends Error {}

const MESSAGES: Record<string, string> = {
  date_taken: '이 날짜의 타운홀이 이미 있어요.',
  invalid_date: '날짜를 다시 확인해 주세요.',
  invalid_url: 'http 또는 https로 시작하는 주소를 넣어 주세요.',
  materials_full: '타운홀 하나에 자료를 더 넣을 수 없어요.',
  unsupported_file: '지원하지 않는 파일 형식이에요.',
  pdf_only: 'PDF 파일만 붙일 수 있어요.',
  pdf_not_needed: 'Keynote 자료에만 PDF 버전을 붙일 수 있어요.',
  conversion_unavailable: '지금은 자동 변환을 쓸 수 없어요. PDF를 직접 붙여 주세요.',
  conversion_not_allowed: '이미 변환 중이거나 PDF가 있어요. 새로고침해 주세요.',
  too_large: '파일이 너무 커요.',
  empty_file: '빈 파일이에요.',
  storage_failed: '파일 저장에 실패했어요. 다시 시도해 주세요.',
  upload_failed: '업로드가 끊겼어요. 다시 시도해 주세요.',
  not_owner: '본인이 등록한 타운홀만 수정할 수 있어요.',
  employee_forbidden: '전사 직원만 사용할 수 있어요.',
  not_found: '타운홀을 찾을 수 없어요. 새로고침해 주세요.',
}

function failure(status: number, code?: string): TownhallError {
  return new TownhallError(MESSAGES[code ?? ''] ?? `요청에 실패했어요 (${status}).`)
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  })
  const data = (await res.json().catch(() => null)) as { error?: string } | null
  if (!res.ok) throw failure(res.status, data?.error)
  return data as T
}

const base = (id: string) => `/api/townhalls/${encodeURIComponent(id)}`

export function fetchTownhalls(): Promise<TownhallList> {
  return json('/api/townhalls')
}

export interface TownhallInput {
  date: string
  title: string
  presenters: string
  agenda: string[]
}

export function createTownhall(input: TownhallInput): Promise<TownhallRow> {
  return json('/api/townhalls', { method: 'POST', body: JSON.stringify(input) })
}

export function updateTownhall(
  id: string,
  input: TownhallInput,
): Promise<TownhallRow> {
  return json(base(id), { method: 'PATCH', body: JSON.stringify(input) })
}

export function deleteTownhall(id: string): Promise<{ ok: true }> {
  return json(base(id), { method: 'DELETE' })
}

export function addLink(id: string, input: { url: string; name: string }): Promise<TownhallRow> {
  return json(`${base(id)}/materials/link`, { method: 'POST', body: JSON.stringify(input) })
}

export function deleteMaterial(id: string, materialId: string): Promise<{ ok: true }> {
  return json(materialBase(id, materialId), { method: 'DELETE' })
}

/** fetch 는 업로드 진행률을 주지 않으므로 XHR 을 쓴다. */
function upload(url: string, file: File, onProgress: (ratio: number) => void): Promise<TownhallRow> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${url}?filename=${encodeURIComponent(file.name)}`)
    xhr.responseType = 'json'
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as TownhallRow)
      else reject(failure(xhr.status, (xhr.response as { error?: string } | null)?.error))
    }
    xhr.onerror = () => reject(failure(0, 'upload_failed'))
    xhr.send(file)
  })
}

export function uploadFile(
  id: string,
  file: File,
  onProgress: (ratio: number) => void,
): Promise<TownhallRow> {
  return upload(`${base(id)}/materials/file`, file, onProgress)
}

const materialBase = (id: string, materialId: string) =>
  `${base(id)}/materials/${encodeURIComponent(materialId)}`

export interface MaterialMeta {
  presenter: string
  description: string
}

export function updateMaterial(id: string, materialId: string, meta: MaterialMeta): Promise<TownhallRow> {
  return json(materialBase(id, materialId), { method: 'PATCH', body: JSON.stringify(meta) })
}

/** Keynote 자료에 PDF 버전을 붙인다(있으면 교체). */
export function uploadPdf(
  id: string,
  materialId: string,
  file: File,
  onProgress: (ratio: number) => void,
): Promise<TownhallRow> {
  return upload(`${materialBase(id, materialId)}/pdf`, file, onProgress)
}

/** Keynote 자동 변환 다시 하기. */
export function convertPdf(id: string, materialId: string): Promise<TownhallRow> {
  return json(`${materialBase(id, materialId)}/convert`, { method: 'POST' })
}

export function deletePdf(id: string, materialId: string): Promise<{ ok: true }> {
  return json(`${materialBase(id, materialId)}/pdf`, { method: 'DELETE' })
}

/**
 * format 을 안 주면 서버가 고른다 — PDF 버전이 있으면 Windows 는 PDF, 나머지는 원본.
 */
export function materialUrl(
  id: string,
  materialId: string,
  download = false,
  format?: 'pdf' | 'original',
): string {
  const q = new URLSearchParams()
  if (download) q.set('download', '1')
  if (format) q.set('format', format)
  const qs = q.toString()
  return `${materialBase(id, materialId)}/open${qs ? `?${qs}` : ''}`
}
